import { useState } from "react";
import { Button, Menu } from "@/deps/ui-library";
import { api } from "@/api";
import type { ReportExport } from "@/api/types";
import { toCsvBlob } from "./csv";

function saveFile(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

/** A report leaves the screen as Excel or CSV, and never as a PDF (ADR-0032). */
export function DownloadMenu({
  title,
  period,
  exportable,
}: {
  title: string;
  period?: string;
  /** What Download writes. Absent while there is nothing to write. */
  exportable?: () => ReportExport;
}) {
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const download = async (format: "xlsx" | "csv") => {
    if (!exportable) return;
    setSaving(true);
    setRefused(null);
    try {
      const report = exportable();
      const stem = `${title} ${period ?? ""}`.trim().replace(/[^\p{L}\p{N}]+/gu, "-");
      saveFile(
        format === "csv" ? toCsvBlob(report) : await api.reports.exportToExcel(report),
        `${stem}.${format}`,
      );
    } catch (error) {
      setRefused(error instanceof Error ? error.message : "That could not be downloaded.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {refused ? (
        <span role="alert" className="text-bad-text text-xs">
          {refused}
        </span>
      ) : null}
      <Menu.Root positioning={{ placement: "bottom-end", gutter: 6 }}>
        <Menu.Trigger asChild>
          <Button
            colorScheme="default"
            variant="outline"
            size="sm"
            leftIcon="Download"
            disabled={!exportable || saving}
          >
            {saving ? "Downloading" : "Download"}
          </Button>
        </Menu.Trigger>
        <Menu.Content>
          <Menu.Item value="xlsx" onClick={() => void download("xlsx")}>
            Excel
          </Menu.Item>
          <Menu.Item value="csv" onClick={() => void download("csv")}>
            CSV
          </Menu.Item>
        </Menu.Content>
      </Menu.Root>
    </>
  );
}
